// 이어 보기: every page of an output, large, one after another, the way the book is read.
// The output panel opens it at a page; it keeps track of the page in view (and says which
// Studio page prints it), loads pictures only near the view, and moves page by page with
// the buttons or ←/→. A page no Studio page prints says instead which chapter it is and what
// that is made of. Opened for editing (an output that lays out its own pages), the page in view
// has its parts drawn over it (output-parts.mjs) and every text it prints beside it (이 쪽의 글,
// output-page-texts.mjs) — or, as 틀 디자인, the elements of the layout it is drawn from to move and
// size (output-page-design.mjs) — and the pages of its page template are stepped through.
import { hasParts, mountPartsOverlay, partsOf } from './output-parts.mjs';
import { mountPageTexts } from './output-page-texts.mjs';
import { mountPageDesign } from './output-page-design.mjs';

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}

/**
 * onBack(): leave for the page list. onEdit(sectionId): open the Studio page that prints the
 * page in view. onMove(number): the page in view changed. onPageEdited(): a text or a layout value
 * of the page was saved or undone beside it. onDesignPreview(): 틀 디자인 asks for a preview.
 */
export function mountOutputReader({ onBack, onEdit, onMove = () => {}, onPageEdited = () => {}, onDesignPreview = () => {} }) {
  const back = node('button', { type: 'button', class: 'es-output-close', 'data-testid': 'studio-output-reader-back', text: '목록으로' });
  const previous = node('button', { type: 'button', class: 'es-output-close', 'data-testid': 'studio-output-reader-prev', 'aria-label': '이전 쪽', text: '‹ 이전' });
  const position = node('span', { class: 'es-output-reader-position', 'aria-live': 'polite', 'data-testid': 'studio-output-reader-position' });
  const next = node('button', { type: 'button', class: 'es-output-close', 'data-testid': 'studio-output-reader-next', 'aria-label': '다음 쪽', text: '다음 ›' });
  const nextChanged = node('button', { type: 'button', class: 'es-output-action', 'data-testid': 'studio-output-reader-next-changed', text: '다음 바뀐 쪽' });
  const edit = node('button', { type: 'button', class: 'es-output-action', 'data-testid': 'studio-output-reader-edit', text: '이 쪽 편집하기' });
  const about = node('span', { class: 'es-output-reader-about', 'data-testid': 'studio-output-reader-about', hidden: '' });
  // The page template of the page in view, and a step between its pages (대운 한 편 · 4/10).
  const templatePrevious = node('button', { type: 'button', class: 'es-output-close', 'data-testid': 'studio-output-template-prev', 'aria-label': '이 틀의 이전 쪽', text: '‹' });
  const templatePosition = node('span', { class: 'es-output-reader-template', 'data-testid': 'studio-output-template-position' });
  const templateNext = node('button', { type: 'button', class: 'es-output-close', 'data-testid': 'studio-output-template-next', 'aria-label': '이 틀의 다음 쪽', text: '›' });
  const templateStep = node('span', { class: 'es-output-reader-template-step', hidden: '' }, [templatePrevious, templatePosition, templateNext]);
  const scroller = node('div', { class: 'es-output-reader-pages', tabindex: '0', 'aria-label': '이어 보기' });
  const overlay = mountPartsOverlay();
  // A text saved beside the page is marked on it too; a text's row and its box light up together.
  const texts = mountPageTexts({ onEdited: (address, pending) => { overlay.markAddress(address, pending); onPageEdited(address); },
    onHover: (partIndex, on) => overlay.highlight(partIndex, on) });
  const design = mountPageDesign({ onEdited: () => onPageEdited(), onPreview: () => onDesignPreview() });
  // Beside the page: its texts, or its layout's design.
  const tab = (id, text) => node('button', { type: 'button', role: 'tab', class: 'es-page-side-tab', 'data-side': id, 'data-testid': `studio-page-side-${id}`, text });
  const tabs = node('div', { class: 'es-page-side-tabs', role: 'tablist', 'aria-label': '이 쪽 편집' }, [tab('texts', '이 쪽의 글'), tab('design', '캔버스 디자인')]);
  const side = node('div', { class: 'es-page-side', hidden: '' }, [tabs, texts.element, design.element]);
  const element = node('section', { class: 'es-output-reader', hidden: '', 'data-testid': 'studio-output-reader' }, [
    node('div', { class: 'es-output-reader-bar' }, [back, previous, position, next, templateStep, nextChanged, edit, about]),
    node('div', { class: 'es-output-reader-body' }, [scroller, side]),
  ]);
  let pages = [], figures = [], index = 0, targetOf = () => null, canvasPageOf = () => null, canOpen = true, labels = { linked: '', data: '' }, frame = 0;
  // Editing: the page editor, the page templates to step through, the output's kinds of part, and
  // the page whose parts and texts are shown.
  let editing = false, pageEditor = null, templates = [], partKinds = {}, shown = -1, asked = 0;
  // 틀 디자인: the output's editor of its layouts' values, the names of their choices, and which side is open.
  let layoutEditor = null, layoutChoices = {}, designOptions = {}, sideOpen = 'texts';
  let renderedPages = new Map();
  function clearRendered() { for (const value of renderedPages.values()) value.then(api => api?.dispose()); renderedPages.clear(); }
  function openSide(id) {
    sideOpen = id;
    for (const button of tabs.children) button.setAttribute('aria-selected', String(button.dataset.side === id));
    element.dataset.side = id;
  }
  for (const button of tabs.children) button.addEventListener('click', () => {
    if (sideOpen === button.dataset.side) return;
    openSide(button.dataset.side);
    if (editing && pages[index]) void showPage(pages[index], figures[index]);
  });
  openSide('texts');
  const templateOf = number => templates.find(template => template.pages.includes(number)) ?? null;

  const figureOf = page => node('figure', { class: `es-output-reader-page${page.changed ? ' es-output-reader-page--changed' : ''}`, 'data-page': String(page.number) }, [
    // The page's own shape (a book page, a wide canvas frame, a long document) keeps the space it will take;
    // its thumbnail, already loaded for the page list, stands in until the large page comes (a preview's is
    // drawn when it is first asked for).
    node('div', { class: 'es-output-reader-frame' }, [
      ...(page.render ? [node('div', { class: 'es-page-native', style: `aspect-ratio:${page.width}/${page.height};width:100%;`, 'data-testid': 'studio-page-native' })] : [node('img', { alt: `${page.number}쪽 ${page.title}`, width: String(page.width ?? 760), height: String(page.height ?? 1078), decoding: 'async',
        ...(page.large && page.image ? { style: `background-image: url(${JSON.stringify(page.image)})` } : {}) })]),
    ]),
    node('figcaption', {}, [node('strong', { text: `${page.number}쪽` }), node('span', { text: page.title }),
      ...(page.changed ? [node('span', { class: 'es-output-badge es-output-badge--changed', text: '바뀜' })] : []),
      ...(page.chapter ? [node('span', { class: 'es-output-page-chapter', text: page.chapter.name })] : []),
      // A page of a chapter can still print the template's text (a worksheet prints the advice).
      ...((targetOf(page) ? labels.linked : page.chapter ? '' : labels.data) ? [node('span', { class: 'es-output-page-kind', text: targetOf(page) ? labels.linked : labels.data })] : [])]),
  ]);

  // The page in view is the last one whose top has passed a third of the way down.
  function pageInView() {
    const line = scroller.scrollTop + scroller.clientHeight / 3;
    let found = 0;
    for (let at = 0; at < figures.length && figures[at].offsetTop <= line; at += 1) found = at;
    return found;
  }

  function loadNear() {
    const top = scroller.scrollTop - scroller.clientHeight, bottom = scroller.scrollTop + scroller.clientHeight * 2;
    figures.forEach((figure, at) => {
      const image = figure.querySelector('img');
      if (figure.offsetTop + figure.offsetHeight >= top && figure.offsetTop <= bottom) {
        const page = pages[at];
        if (page.render && !renderedPages.has(figure)) {
          const host = figure.querySelector('.es-page-native');
          renderedPages.set(figure, page.render(host).catch(error => { host.textContent = error.message; return null; }));
        } else if (image && !image.getAttribute('src')) image.src = page.large ?? page.image;
      }
    });
  }

  // The page being read and how far down it the view is, kept when the pages change size under the
  // view (the page's texts appearing beside it on a narrow screen, the window resized), which the
  // scroll position alone does not survive. Only a scroll that is not the one placed here moves it.
  let anchor = null, placedTop = null;
  function remember() {
    const at = pageInView(), figure = figures[at];
    if (figure) anchor = { at, fraction: Math.max(0, (scroller.scrollTop + 12 - figure.offsetTop) / (figure.offsetHeight || 1)) };
  }
  function place() {
    const figure = anchor && figures[anchor.at];
    if (!figure) return;
    scroller.scrollTop = figure.offsetTop - 12 + anchor.fraction * figure.offsetHeight;
    placedTop = scroller.scrollTop;
  }
  const resizeObserver = new ResizeObserver(() => { if (!element.hidden && anchor) { place(); update(anchor.at); } });
  resizeObserver.observe(scroller);
  scroller.addEventListener('scroll', () => {
    frame ||= requestAnimationFrame(() => { if (scroller.scrollTop !== placedTop) remember(); update(); });
  });

  /** Shows what the page in view is (or `intended`, the page just gone to, whatever the layout says yet). */
  function update(intended = null) {
    frame = 0;
    loadNear();
    const found = intended ?? anchor?.at ?? pageInView();
    const page = pages[found];
    if (!page) return;
    const moved = found !== index;
    index = found;
    position.textContent = `${page.number} / ${pages.length}쪽 · ${page.title}`;
    previous.disabled = index === 0;
    next.disabled = index === pages.length - 1;
    // Editing, the button names the canvas page it opens (the Studio page whose text this page prints);
    // a page the canvas has no page for (one the output makes from its own manuscripts) says so there,
    // and the line under it says where its text is edited, instead of the button going away.
    const target = targetOf(page), canvas = target ? canvasPageOf(target) : null;
    // A content edited on its output's pages has no Studio page to open.
    edit.hidden = !canOpen || (!target && !editing);
    edit.disabled = !target;
    edit.textContent = !editing ? '이 쪽 편집하기' : canvas ? `캔버스 ${canvas.number}쪽 열기` : target ? '캔버스에서 열기' : '캔버스 페이지 없음';
    edit.title = !editing ? ''
      : target ? `${canvas ? `캔버스 ${canvas.number}쪽 ‘${canvas.title}’ — ` : ''}이 쪽이 싣는 상품 글이 있는 제작실 페이지입니다. 원본 캔버스의 요소를 편집합니다.`
      : '이 쪽은 제작실 캔버스에 대응하는 페이지가 없습니다. 글은 오른쪽 ‘이 쪽의 글’에서 고칩니다.';
    // A page no Studio page prints: which chapter it is, and what that is made of.
    const outside = !target && page.chapter;
    about.hidden = !outside;
    about.textContent = outside ? `${page.chapter.name} · ${page.chapter.note}` : '';
    nextChanged.hidden = !pages.some(item => item.changed);
    const template = templateOf(page.number);
    templateStep.hidden = !template || template.pages.length < 2;
    if (template) {
      const at = template.pages.indexOf(page.number);
      templatePosition.textContent = `${template.title} · ${at + 1}/${template.pages.length}`;
      templatePrevious.disabled = at <= 0; templateNext.disabled = at >= template.pages.length - 1;
    }
    if (editing && shown !== index) { shown = index; void showPage(page, figures[index]); }
    if (moved) onMove(page.number);
  }
  /** The page's parts drawn over it and its texts beside it, from one read of its parts and of the edits waiting. */
  async function showPage(page, figure) {
    const mine = ++asked;
    overlay.clear(); design.clear();
    if (sideOpen === 'design') texts.clear();
    if (!hasParts(page)) { openSide('texts'); texts.empty(page); return; }
    if (sideOpen === 'texts') texts.loading(page);
    let found, ready = false, pending = [], designReady = false, designPending = [];
    try {
      found = await partsOf(page);
      if (sideOpen === 'design') {
        designReady = Boolean(layoutEditor) && await layoutEditor.available().catch(() => false);
        if (designReady) designPending = await layoutEditor.list();
      } else {
        ready = Boolean(pageEditor) && await pageEditor.available();
        if (ready) pending = await pageEditor.list();
      }
    } catch (error) { if (mine === asked) { openSide('texts'); texts.empty(page, error.message); } return; }
    if (mine !== asked) return;
    if (!found) { openSide('texts'); texts.empty(page); return; }
    if (sideOpen === 'design') {
      await design.show(page, figure, found, { editor: designReady ? layoutEditor : null, pending: designPending, choices: layoutChoices, pageSize: found.size, design: designOptions });
      return;
    }
    const owned = part => ready && (part.edits ?? []).some(pageEditor.owns);
    overlay.draw(figure, found, { kinds: partKinds, editable: owned,
      edited: part => owned(part) && part.edits.some(address => pending.some(edit => edit.address === address)),
      onPick: partIndex => texts.focus(partIndex), onHover: (partIndex, on) => texts.light(partIndex, on) });
    await texts.show(page, found, { editor: ready ? pageEditor : null, pending, kinds: partKinds });
  }
  function stepTemplate(offset) {
    const template = templateOf(pages[index]?.number);
    const at = template ? template.pages.indexOf(pages[index].number) + offset : -1;
    if (template && at >= 0 && at < template.pages.length) go(pages.findIndex(page => page.number === template.pages[at]));
  }
  templatePrevious.addEventListener('click', () => stepTemplate(-1));
  templateNext.addEventListener('click', () => stepTemplate(1));

  function go(at) {
    const target = Math.max(0, Math.min(at, figures.length - 1));
    if (!figures[target]) return;
    anchor = { at: target, fraction: 0 };
    place();
    update(target);
  }
  previous.addEventListener('click', () => go(index - 1));
  next.addEventListener('click', () => go(index + 1));
  nextChanged.addEventListener('click', () => {
    const after = pages.findIndex((page, at) => at > index && page.changed);
    go(after >= 0 ? after : pages.findIndex(page => page.changed));
  });
  edit.addEventListener('click', () => { const target = targetOf(pages[index]); if (target) onEdit(target); });
  back.addEventListener('click', () => onBack());
  element.addEventListener('keydown', event => {
    // Keys typed into a text beside the page stay there: the arrows move in the text, and Escape
    // only leaves it (the list would drop what is not saved yet).
    if (event.target.closest?.('textarea, input')) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); event.target.blur(); scroller.focus({ preventScroll: true }); }
      return;
    }
    if (event.key === 'ArrowRight') { event.preventDefault(); go(index + 1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); go(index - 1); }
    // Escape goes back to the page list — and only that: where the panel sits in the editor,
    // the editor's own Escape would also fold its page list away.
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onBack(); }
  });

  /**
   * Shows `list` from the page numbered `start`; `targetOf(page)` is its Studio page or null (and
   * `canvasPageOf(id)` that page's { number, title }), `labels` name the two kinds of page ({ linked, data }). With `editing`, the page in view has its
   * parts drawn over it and every text it prints beside it, edited with `pageEditor`; `templates`
   * are its page templates to step through, `partKinds` what its kinds of part are.
   */
  function open(list, { start = list[0]?.number, targetOf: studioPage, canvasPageOf: numbered = () => null, canOpenStudioPage = true, labels: kinds = labels,
    editing: edit = false, pageEditor: joined = null, templates: pageTemplates = [], partKinds: namedKinds = {},
    layoutEditor: layouts = null, layoutChoices: choices = {}, designOptions: options = {} }) {
    pages = list; targetOf = studioPage; canvasPageOf = numbered; canOpen = canOpenStudioPage; labels = kinds; index = -1;
    editing = edit && Boolean(joined || layouts); pageEditor = joined; templates = pageTemplates; partKinds = namedKinds; shown = -1;
    layoutEditor = layouts; layoutChoices = choices; designOptions = options;
    tabs.querySelector('[data-side="texts"]').hidden = !joined;
    if (!joined) openSide('design');
    element.toggleAttribute('data-editing', editing);
    side.hidden = !editing;
    overlay.clear(); design.clear();
    if (!editing) texts.clear();
    clearRendered();
    figures = pages.map(figureOf);
    scroller.replaceChildren(...figures);
    element.hidden = false;
    go(Math.max(0, pages.findIndex(page => page.number === start)));
    scroller.focus({ preventScroll: true });
  }
  function close() { clearRendered(); element.hidden = true; scroller.replaceChildren(); figures = []; anchor = null; asked += 1; overlay.clear(); texts.clear(); design.clear(); shown = -1; }
  function dispose() { close(); design.dispose(); resizeObserver.disconnect(); cancelAnimationFrame(frame); }
  /** Reads from the page numbered `number`, when it is among the pages shown. */
  function goTo(number) { const at = pages.findIndex(page => page.number === number); if (at >= 0) go(at); return at >= 0; }

  return { element, open, close, dispose, goTo, flush: () => design.flush(), get page() { return pages[index]?.number ?? null; }, get isOpen() { return !element.hidden; } };
}
